import { useMemo } from 'react';
import type { PregnancyResult } from '~/utils/pregnancyCalculations';
import { getTrimesterInfo } from '~/utils/pregnancyCalculations';

interface PregnancyProgressProps {
  result: PregnancyResult;
}

export default function PregnancyProgress({ result }: PregnancyProgressProps) {
  const trimesterInfo = getTrimesterInfo(result.trimester);
  
  if (!trimesterInfo) {
    return (
      <div className="text-center text-red-500 py-4">
        无法获取孕期信息，请检查数据
      </div>
    );
  }
  
  const progressInfo = useMemo(() => {
    const totalDays = 280; // 40周 = 280天
    const progressPercentage = Math.min((result.daysPregnant / totalDays) * 100, 100);
    
    // 计算孕期阶段进度
    const trimesterProgress = {
      first: Math.min(Math.max((result.daysPregnant / 84) * 100, 0), 100), // 0-12周
      second: Math.min(Math.max(((result.daysPregnant - 84) / 98) * 100, 0), 100), // 13-27周
      third: Math.min(Math.max(((result.daysPregnant - 182) / 98) * 100, 0), 100) // 28-40周
    };
    
    return {
      progressPercentage,
      trimesterProgress
    };
  }, [result.daysPregnant]);

  const getTrimesterIcon = (trimester: string) => {
    switch (trimester) {
      case '孕早期': return '🌱';
      case '孕中期': return '🤱';
      case '孕晚期': return '👶';
      default: return '📅';
    }
  };

  const getTrimesterColor = (trimester: string) => {
    switch (trimester) {
      case '孕早期': return 'from-green-400 to-green-600';
      case '孕中期': return 'from-blue-400 to-blue-600';
      case '孕晚期': return 'from-purple-400 to-purple-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* 主要进度显示 */}
      <div className="text-center">
        <div className="text-6xl mb-4">
          {getTrimesterIcon(trimesterInfo?.name || '')}
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          孕 {result.currentWeek} 周 {result.currentDay} 天
        </h3>
        <p className="text-gray-600">
          {trimesterInfo?.name || ''} · 已怀孕 {result.daysPregnant} 天
        </p>
      </div>

      {/* 整体进度条 */}
      <div className="space-y-3">
        <div className="flex justify-between text-sm text-gray-600">
          <span>怀孕进度</span>
          <span>{progressInfo.progressPercentage.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div 
            className={`h-full bg-gradient-to-r ${getTrimesterColor(trimesterInfo?.name || '')} transition-all duration-500 ease-out`}
            style={{ width: `${progressInfo.progressPercentage}%` }}
          >
            <div className="h-full bg-white/20 animate-pulse"></div>
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>受孕</span>
          <span>预产期</span>
        </div>
      </div>

      {/* 孕期阶段进度 */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { name: '孕早期', weeks: '1-12周', progress: progressInfo.trimesterProgress.first, icon: '🌱' },
          { name: '孕中期', weeks: '13-27周', progress: progressInfo.trimesterProgress.second, icon: '🤱' },
          { name: '孕晚期', weeks: '28-40周', progress: progressInfo.trimesterProgress.third, icon: '👶' }
        ].map((stage, index) => {
          const isActive = trimesterInfo?.name === stage.name;
          const isCompleted = stage.progress >= 100;
          
          return (
            <div key={index} className={`p-4 rounded-xl border-2 transition-all ${
              isActive ? 'border-primary bg-primary/5 shadow-lg' :
              isCompleted ? 'border-green-300 bg-green-50' :
              'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center">
                <div className="text-2xl mb-2">{stage.icon}</div>
                <div className={`text-sm font-medium ${
                  isActive ? 'text-primary' :
                  isCompleted ? 'text-green-600' :
                  'text-gray-600'
                }`}>
                  {stage.name}
                </div>
                <div className="text-xs text-gray-500 mb-3">{stage.weeks}</div>
                
                {/* 小进度条 */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${
                      isActive ? 'bg-primary' :
                      isCompleted ? 'bg-green-500' :
                      'bg-gray-400'
                    }`}
                    style={{ width: `${Math.min(stage.progress, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {stage.progress.toFixed(0)}%
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 重要数据卡片 */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-4 rounded-xl border border-pink-200">
          <div className="text-center">
            <div className="text-2xl mb-2">📅</div>
            <div className="text-sm text-pink-600 font-medium">预产期</div>
            <div className="text-lg font-bold text-pink-800">
              {result.dueDate.toLocaleDateString('zh-CN', { 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            <div className="text-xs text-pink-600">
              还有 {result.daysUntilDue} 天
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
          <div className="text-center">
            <div className="text-2xl mb-2">💝</div>
            <div className="text-sm text-blue-600 font-medium">受孕日期</div>
            <div className="text-lg font-bold text-blue-800">
              {result.conceptionDate.toLocaleDateString('zh-CN', { 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            <div className="text-xs text-blue-600">
              约 {result.daysPassed} 天前
            </div>
          </div>
        </div>
      </div>

      {/* 当前孕期特点 */}
      <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
        <h4 className="text-lg font-bold text-purple-800 mb-3 flex items-center">
          <span className="mr-2">{getTrimesterIcon(trimesterInfo?.name || '')}</span>
          {trimesterInfo?.name || ''}特点
        </h4>
        <p className="text-purple-700 mb-4">{trimesterInfo?.description || ''}</p>
        
        <div className="space-y-3">
          <div>
            <h5 className="font-medium text-purple-800 mb-2">关键发育</h5>
            <ul className="text-sm text-purple-700 space-y-1">
              {(trimesterInfo?.keyPoints || []).map((point, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h5 className="font-medium text-purple-800 mb-2">注意事项</h5>
            <ul className="text-sm text-purple-700 space-y-1">
              {(trimesterInfo?.tips || []).map((rec, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* 周期里程碑预览 */}
      <div className="bg-white p-6 rounded-xl border border-gray-200">
        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
          <span className="mr-2">🎯</span>
          即将到来的里程碑
        </h4>
        <div className="space-y-3">
          {/* 里程碑功能暂时移除，等待后续实现 */}
          <div className="text-center text-gray-500 py-4">
            里程碑功能开发中...
          </div>
        </div>
      </div>
    </div>
  );
}